import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ApiUrlService } from '@core/services/common/api-url.service';
import { CurrencyPreferenceService } from '@shared/services/currency/currency-preference.service';
import { LocalStorageService } from '@shared/services/storage';
import { Observable } from 'rxjs';

import { map } from 'rxjs/operators';

import { isSameCartItem } from './cart-item-identity';

export type status = 'unavailable' | undefined;

export interface AdditionalInformation {
  timeOfCreation: string;
}

export interface BasketItem {
  objectId: number;
  quantity: number;
  type: string;
  /**
   * Wariant pracy — jednostka handlowa, którą kupujący wybrał w konfiguratorze.
   * Puste dla pracy w trybie prostym i dla pozycji dodanych przed wprowadzeniem edycji.
   */
  variantId?: number | null;
  id?: number;
  updatedAt?: Date;
  name?: string;
  media?: string;
  slug?: string;
  inventoryNumberFull?: string;
  basketId?: number;
  inventoryNumber?: string;
  title?: string;
  artist?: string;
  price?: number;
  currency?: string;
  transportPrice?: number;
  transportCurrency?: string;
  createdAt?: Date;
  maxQuantity?: number;
  status?: status;
  additionalInformations: AdditionalInformation;
  // delivery
  collectionOnSite?: number;
  domesticDelivery?: number;
  euDelivery?: number;
  worldDelivery?: number;
  defaultShippingCurrency: string;
  insurance: string;
  parentType: string;
  grupID: string;
}

export interface Basket {
  userId: number;
  id: number;
  createdAt: Date;
  basketItems: BasketItem[];
  parentType?: string;
  grupID?: string;
}

export interface BasketItemSimple {
  objectId: number;
  type: string;
  quantity: number;
  parentType: string;
  grupID: string;
  variantId?: number | null;
}

@Injectable()
export class ApiBasketService {
  constructor(
    private httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private currencyPreferenceService: CurrencyPreferenceService,
    private localStorageService: LocalStorageService,
  ) {
  }

  basket(): Observable<Basket> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.get<any>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/basket`)).pipe(
      map((cart) => {
        const items = cart.basketItems.map((item) => {
          const {artist, media} = item;
          const basketItem: BasketItem = {
            ...item,
            artist: `${artist.firstName ?? ''} ${artist.lastName ?? ''}`,
            media: Array.isArray(media) && media.length > 0 ? media[0] : media,
          };
          return basketItem;
        });
        const _cart: Basket = {
          id: cart.id,
          createdAt: cart.createdAt,
          userId: cart.userId,
          basketItems: items,
        };
        return _cart;
      }),
    );
  }

  itemPrototype(item: BasketItemSimple): Promise<BasketItem> {
    const aliUrl = this.apiUrlService.getApiUrl();
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient
      .post<any>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/basket/basket-item-prototype`), {
        ...item,
        headers,
      })
      .pipe(
        map((_item) => {
          const {artist, media} = _item;
          const basketItem: BasketItem = {
            ..._item,
            artist: `${artist.firstName ?? ''} ${artist.lastName ?? ''}`,
            media: Array.isArray(media) && media.length > 0 ? media[0] : media,
          };
          return basketItem;
        }),
      )
      .toPromise();
  }

  delete(): Observable<{ deletedItems: number }> {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.delete<{ deletedItems: number }>(
      `${aliUrl}client/basket`,
    );
  }

  addItem(basket: {
    items: BasketItemSimple[];
  }, test?: any): Observable<{ inserted_items: number }> {
    const aliUrl = this.apiUrlService.getApiUrl();

    // Odsiewamy powtorzenia po pelnej tozsamosci pozycji. Filtr po samym objectId
    // wyrzucal z zadania druga edycje tej samej pracy — kupujacy dokladal ja do koszyka
    // i nigdy nie docierala do backendu.
    basket.items = (basket?.items || []).filter((item, idx, arr) => {
      return arr.findIndex(it => isSameCartItem(it, item)) === idx;
    });

    return this.httpClient.post<{ inserted_items: number }>(
      `${aliUrl}client/basket/basket-item`,
      {...basket},
    );
  }

  deleteItem(basketItemId: number) {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient.delete(
      `${aliUrl}client/basket/basket-item/${basketItemId}`,
    );
  }

  updateQuantity(basketItemId: number, body: { quantity: number }): any {
    const aliUrl = this.apiUrlService.getApiUrl();
    return this.httpClient
      .put<any>(
        `${aliUrl}client/basket/basket-item/${basketItemId}`,
        {body},
      )
      .pipe(map((next: any) => next.status));
  }

  getItemBaskets(sendData: any): Observable<any[]> {
    const dimensionsData = JSON.parse(JSON.stringify(sendData));
    const aliUrl = this.apiUrlService.getApiUrl();
    const headers = new HttpHeaders({'Content-Type': 'application/json'});
    return this.httpClient.post<any>(this.currencyPreferenceService.appendCurrencyToUrl(`${aliUrl}client/basket/get-baskets-item`), {
      dimensionsData,
      headers,
    });
  }

  /**
   * ZADANIE B5.4. Pomocnik dokładający nagłówek autoryzacji ZNIKNĄŁ — robi to teraz
   * interceptor uwierzytelniania z modułu głównego, dla wszystkich żądań naraz.
   *
   * Zmiana ma jeden konkretny skutek: odpowiedź 401 na tych trasach uruchamia ODNOWIENIE
   * sesji i ponowienie żądania, a nie cichą odmowę. Wcześniej token jechał ręcznie, więc
   * żądania działały — ale nikt nie łapał wygaśnięcia, a interceptor, który miał to robić,
   * był zarejestrowany w łańcuchu, z którego nie korzystał żaden serwis.
   */


}
